import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Reveal from "../components/common/Reveal";
import { proxied } from "../lib/assets";

export default function About() {
  return (
    <div data-testid="page-about">
      {/* Hero */}
      <section className="relative py-24 lg:py-36 px-6 lg:px-10 bg-[#F3EDE1] overflow-hidden">
        <div className="max-w-[1500px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-end">
          <Reveal className="lg:col-span-7">
            <div className="star-sep mb-8">About Me</div>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-8xl font-light leading-[0.98]" data-testid="about-title">
              Chief Escape Artist,<br /><span className="italic text-teal">LuxeTini Escapes.</span>
            </h1>
            <p className="mt-8 text-lg text-muted max-w-xl leading-relaxed font-light">
              Hi, I'm Tricia. I don't just plan travel — I live it. Here's a little bit about the person behind your next escape.
            </p>
          </Reveal>
          <Reveal className="lg:col-span-5" delay={140}>
            <div className="tile aspect-[4/5] overflow-hidden">
              <img
                src={proxied("https://customer-assets.emergentagent.com/job_escape-design-lab/artifacts/ezqrxt9s_Screenshot%202026-05-19%20at%203.01.48%E2%80%AFPM.png")}
                alt="Tricia — Chief Escape Artist of LuxeTini Escapes"
                className="w-full h-full object-cover"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Story */}
      <section className="py-24 lg:py-36 px-6 lg:px-10 bg-ivory">
        <div className="max-w-4xl mx-auto">
          <Reveal>
            <p className="font-serif text-3xl sm:text-4xl italic leading-[1.25] text-ink">
              For over <span className="text-gold not-italic font-normal">30 years</span>, I built my career in accounting and payroll — where details matter, deadlines are non-negotiable, and trust is everything.
            </p>
          </Reveal>

          <div className="mt-12 space-y-8 text-lg text-muted font-light leading-[1.9]">
            <Reveal delay={80}>
              <p>
                I've spent decades managing complexity, solving problems before they happen, and making sure nothing falls through the cracks. That same precision and dedication now fuels every trip I design.
              </p>
            </Reveal>
            <Reveal delay={120}>
              <p>
                But travel? That's always been my heart. I'm a passionate explorer, a scuba diving enthusiast, and someone who believes the best moments in life happen when you step outside of your routine and into something unforgettable. Whether it's diving into crystal-clear waters, discovering hidden gems, or simply slowing down somewhere beautiful — I don't just plan travel, I live it.
              </p>
            </Reveal>
            <Reveal delay={160}>
              <p>
                My experience has also shaped how I approach travel on a deeper level. Traveling alongside my high-functioning disabled spouse has taught me the importance of thoughtful planning, flexibility, and advocacy. It's not just about where you go — it's about how you get there, how you experience it, and making sure every detail supports your comfort and peace of mind.
              </p>
            </Reveal>
            <Reveal delay={200}>
              <p>
                When you work with me, you're not just getting someone who can book a trip — you're getting someone who listens closely, plans thoughtfully, anticipates the "what ifs," and advocates for you every step of the way.
              </p>
            </Reveal>
          </div>

          <Reveal delay={220}>
            <div className="mt-16 border-l-2 border-gold pl-8 py-2">
              <p className="font-serif text-2xl sm:text-3xl italic leading-[1.4] text-ink">
                LuxeTini Escapes was built on the belief that life is too short for ordinary vacations.
              </p>
            </div>
          </Reveal>

          <Reveal delay={260}>
            <p className="mt-16 font-serif text-4xl sm:text-5xl text-ink">
              So go ahead — <span className="italic text-gold">dream big.</span><br />I'll handle the details.
            </p>
            <Link
              to="/plan-your-escape"
              className="mt-10 inline-flex items-center gap-3 h-14 px-8 bg-ink text-ivory hover:bg-teal transition-colors text-[12px] uppercase tracking-[0.28em]"
              data-testid="about-cta"
            >
              Start Planning Your Escape <ArrowUpRight size={16} strokeWidth={1.5} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Values strip */}
      <section className="py-20 px-6 lg:px-10 bg-nightsky text-ivory">
        <div className="max-w-[1500px] mx-auto grid grid-cols-2 lg:grid-cols-4 gap-10">
          {[
            ["01", "Listens Closely", "Your vision comes first. Every question leads to a more personal plan."],
            ["02", "Plans Thoughtfully", "Precision honed over three decades in accounting and payroll."],
            ["03", "Anticipates", "I sweat the 'what ifs' so you never have to."],
            ["04", "Advocates", "If something shifts mid-trip — I'm the one making the call."],
          ].map(([num, title, body], i) => (
            <Reveal key={num} delay={i * 80}>
              <div className="border-t border-gold/40 pt-6">
                <div className="text-gold text-xs tracking-[0.3em]">{num}</div>
                <div className="font-serif text-2xl mt-3 text-ivory">{title}</div>
                <p className="mt-3 text-sm text-ivory/60 font-light leading-relaxed">{body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>
    </div>
  );
}
